import { Stack } from '@mui/material';
import { Fragment } from 'react';
import { Operator } from '../enum/Operator';
import { Expression, FilterConfiguration } from '../types';
import { getUniqueId } from '../utils';
import { ExpandingIconButton } from './ExpandingIconButton';
import { ExpressionChip } from './ExpressionChip';
import { FilterEditor } from './FilterEditor';
import { OperatorChip } from './OperatorChip';

/**
 * Combiner is the particular AND/OR nesting that Expressions can live on.
 * It can have nested Expressions or further nested Combiners.
 */
export type Combiner = {
  id: number;
  operator: Operator;
  subFilters: (Combiner | Expression)[];
};

type CombinerProps = {
  combiner: Combiner;
  onChange: (data: Combiner) => void;
  onRemove: () => void;
  filters: FilterConfiguration[];
  level?: number;
};

const LEVEL_LIMIT = 1;

/** Renders all the AND/OR groups */
export const CombinerComponent = ({ combiner, onChange, onRemove, filters, level = 0 }: CombinerProps) => {
  const subFilters = combiner.subFilters;
  const operator = combiner.operator;

  const handleAddNew = (pos: number, newValue: Expression) => {
    // Generate structure, either expression or combiner
    const value = { ...newValue, id: getUniqueId() };

    let newValues = [...subFilters];
    newValues.splice(pos, 0, value);

    // Flatten subFilters due to ExpressionAdder using Array for newValue
    newValues = newValues.flat();
    onChange({ ...combiner, subFilters: newValues });
  };

  const handleAddNewGroup = (pos: number, newValue: Expression) => {
    const value = { operator: Operator.AND, subFilters: [{ ...newValue, id: getUniqueId() }], id: getUniqueId() } as Combiner;

    let newValues = [...subFilters];
    newValues.splice(pos, 0, value);

    // Flatten subFilters due to ExpressionAdder using Array for newValue
    newValues = newValues.flat();

    onChange({ ...combiner, subFilters: newValues });
  };

  const handleAddExpression = (value: Expression) => {
    const pos = subFilters.length;
    if (level >= LEVEL_LIMIT) {
      handleAddNew(pos, value);
    } else {
      handleAddNewGroup(pos, value);
    }
  };

  const handleChange = (id: number, value: Expression | Combiner) => {
    const newValues = subFilters.map((v) => (v.id === id ? value : v));

    onChange({ ...combiner, subFilters: newValues });
  };

  const handleRemove = (id: number) => {
    const newValues = subFilters.filter((v) => v.id !== id);

    const updatedCombiner = { ...combiner, subFilters: newValues };

    if (!updatedCombiner.subFilters.length) {
      onRemove();
    } else {
      // Propagate to parent
      onChange(updatedCombiner);
    }
  };

  const toggleOperator = (operator: Operator) => {
    onChange({ id: combiner.id, operator, subFilters });
  };

  const containerStyles = {
    p: 0.25,
    borderRadius: '24px',
    backgroundColor: level === 0 ? null : 'white',
    border: level === 0 ? null : '1px solid rgba(0, 0, 0, 0.26)',
  };

  return (
    <Stack direction="row" alignItems="center" gap={0.75} sx={containerStyles}>
      {subFilters.map((value, idx) => {
        const isCombiner = 'operator' in value;
        return (
          <Fragment key={value.id}>
            {idx !== 0 && <OperatorChip operator={operator} onClick={toggleOperator} />}
            {isCombiner ? (
              <CombinerComponent
                filters={filters}
                combiner={value}
                onChange={(data) => handleChange(value.id, data)}
                onRemove={() => handleRemove(value.id)}
                level={level + 1}
              />
            ) : (
              <FilterEditor
                filters={filters}
                initialExpression={value}
                onSubmit={(data) => handleChange(value.id, data)}
                AnchorComponent={({ onClick, expression }) => (
                  <ExpressionChip expression={expression} filters={filters} onClick={onClick} onDelete={() => handleRemove(value.id)} />
                )}
              />
            )}
          </Fragment>
        );
      })}
      <FilterEditor
        key={subFilters.length}
        filters={filters}
        initialExpression={null}
        onSubmit={handleAddExpression}
        AnchorComponent={({ onClick, expression, open }) => {
          const isFirst = subFilters.length === 0;
          const showOperator = open && (!isFirst || level > 0);
          const alwaysExpanded = level === 0;
          return (
            <Stack direction="row" alignItems="center" gap={0.75}>
              {showOperator && <OperatorChip operator={operator} />}
              {open ? (
                <ExpressionChip expression={expression} filters={filters} />
              ) : (
                <ExpandingIconButton variant={isFirst ? 'search' : 'add'} alwaysExpanded={alwaysExpanded} onClick={onClick} />
              )}
              {!isFirst && <ExpandingIconButton variant="remove" alwaysExpanded={alwaysExpanded} onClick={onRemove} />}
            </Stack>
          );
        }}
      />
    </Stack>
  );
};
